import React, { forwardRef } from "react";
import { QRCodeCanvas } from "qrcode.react";

/**
 * Three formats:
 *  - a4 (A4 portrait, 794x1123px) — full page poster
 *  - poster (4x6 inch, 600x900px) — table card
 *  - square (4x4 inch, 600x600px) — square QR card
 *
 * Each template renders at exact pixel size — the same DOM is captured by
 * html-to-image so preview === export, with no scaling artifacts.
 *
 * Fonts: use system-ui / sans-serif fallback. Web fonts are NOT used here 
 * because canvas/foreignObject font metrics can disagree with the browser, 
 * causing letter overlap on export. System fonts are pixel-perfect-safe.
 */

// Size configurations
export const A4 = { w: 794, h: 1123 };      // A4 portrait
export const POSTER = { w: 600, h: 900 };   // 4x6 inch
export const SQUARE = { w: 600, h: 600 };   // 4x4 inch

export const FORMATS = [
  { id: "a4", name: "A4 Poster", size: A4, pdf: { w: 210, h: 297 } },        // mm
  { id: "poster", name: "Table Card · 4×6", size: POSTER, pdf: { w: 102, h: 152 } }, // mm
  { id: "square", name: "Square QR · 4×4", size: SQUARE, pdf: { w: 102, h: 102 } },  // mm
];

const baseFont = "system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif";

function wrapStyle(w, h, bg = "#FFFFFF") {
  return {
    width: w,
    height: h,
    position: "relative",
    overflow: "hidden",
    boxSizing: "border-box",
    background: bg,
    fontFamily: baseFont,
    color: "#1A1A1A",
    // Crucial: avoid sub-pixel font tricks that canvas can mis-measure
    fontKerning: "none",
    textRendering: "geometricPrecision",
  };
}

const LogoBlock = ({ logoUrl, businessName, color = "#1A1A1A", size = 64 }) => {
  if (logoUrl) {
    return (
      <img
        src={logoUrl}
        alt=""
        crossOrigin="anonymous"
        style={{ height: size, maxWidth: size * 2.4, objectFit: "contain", display: "block" }}
      />
    );
  }
  return (
    <div
      style={{
        height: size,
        width: size,
        borderRadius: size * 0.22,
        background: color,
        color: "#fff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: 800,
        fontSize: size * 0.5,
        border: "2px solid #1A1A1A",
        fontFamily: baseFont,
      }}
    >
      {(businessName || "R").charAt(0).toUpperCase()}
    </div>
  );
};

/* ====================== Templates ====================== */

/* ---------- 1. Minimal ---------- */
const MinimalPoster = forwardRef(({ cfg, format = "a4" }, ref) => {
  const sq = format === "square";
  const tbl = format === "poster";
  const a4 = format === "a4";
  
  const size = sq ? SQUARE : (tbl ? POSTER : A4);
  // Increased padding for more breathing room
  const pad = sq ? 36 : (tbl ? 48 : 56);
  const qrSize = sq ? 360 : (tbl ? 320 : 380);
  const headlineSize = sq ? 22 : (tbl ? 26 : 30);
  const businessSize = sq ? 20 : (tbl ? 22 : 26);

  return (
    <div ref={ref} style={wrapStyle(size.w, size.h, "#FFFFFF")}>
      <div style={{ position: "absolute", inset: 0, padding: pad, display: "flex", flexDirection: "column" }}>
        {/* Top */}
        <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
          <LogoBlock logoUrl={cfg.logoUrl} businessName={cfg.businessName} color={cfg.primaryColor} size={sq ? 48 : (tbl ? 52 : 56)} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: sq ? 9 : (tbl ? 10 : 11), letterSpacing: 2, fontWeight: 700, textTransform: "uppercase", color: "#6B6B6B" }}>
              Scan to begin
            </div>
            <div
              style={{
                fontSize: businessSize,
                fontWeight: 800,
                lineHeight: 1.1,
                marginTop: 4,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {cfg.businessName || "Your business"}
            </div>
          </div>
        </div>

        {/* Headline - with increased spacing */}
        {!sq && (
          <div style={{ marginTop: a4 ? 48 : (tbl ? 40 : 36), marginBottom: a4 ? 35 : (tbl ? 30 : 24) }}>
            <div style={{ fontSize: headlineSize, fontWeight: 800, lineHeight: 1.15 }}>{cfg.headline}</div>
            {cfg.subtext && a4 && (
              <div style={{ marginTop: 14, fontSize: 15, color: "#4A4A4A", lineHeight: 1.4 }}>{cfg.subtext}</div>
            )}
          </div>
        )}

        {/* QR - centered with breathing room */}
        <div style={{ 
          flex: 1, 
          display: "flex", 
          alignItems: "center", 
          justifyContent: "center",
          marginTop: sq ? 10 : (tbl ? 20 : 25),
          marginBottom: sq ? 10 : (tbl ? 20 : 25)
        }}>
          <div
            style={{
              padding: sq ? 16 : (tbl ? 20 : 24),
              background: "#FFF",
              borderRadius: 16,
              border: "2px solid #1A1A1A",
              boxShadow: "5px 5px 0 0 #1A1A1A",
            }}
          >
            <QRCodeCanvas value={cfg.publicUrl} size={qrSize} bgColor="#FFFFFF" fgColor="#1A1A1A" level="M" includeMargin={false} />
          </div>
        </div>

        {/* Reward + footer - with clear separation */}
        {cfg.rewardHighlight && (
          <div style={{ textAlign: "center", marginTop: sq ? 8 : (tbl ? 16 : 20), marginBottom: sq ? 8 : (tbl ? 16 : 20) }}>
            <span
              style={{
                display: "inline-block",
                background: cfg.primaryColor,
                color: "#FFF",
                padding: sq ? "6px 14px" : (tbl ? "8px 16px" : "10px 20px"),
                borderRadius: 999,
                fontSize: sq ? 12 : (tbl ? 13 : 15),
                fontWeight: 800,
                border: "2px solid #1A1A1A",
              }}
            >
              {cfg.rewardHighlight}
            </span>
          </div>
        )}
        <div style={{ textAlign: "center", fontSize: sq ? 11 : (tbl ? 12 : 14), fontWeight: 700, color: "#1A1A1A", marginTop: 6 }}>
          Point your phone camera at the QR code
        </div>
      </div>
    </div>
  );
});

/* ---------- 2. Bold Offer ---------- */
const BoldPoster = forwardRef(({ cfg, format = "a4" }, ref) => {
  const sq = format === "square";
  const tbl = format === "poster";
  const a4 = format === "a4";
  
  const size = sq ? SQUARE : (tbl ? POSTER : A4);
  const pad = sq ? 24 : (tbl ? 28 : 32);
  const qrSize = sq ? 340 : (tbl ? 280 : 320);
  const heroH = sq ? 200 : (tbl ? 260 : 320);

  return (
    <div ref={ref} style={wrapStyle(size.w, size.h, "#FFFDF5")}>
      {/* Hero band */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: heroH,
          background: cfg.primaryColor,
          borderBottom: "3px solid #1A1A1A",
        }}
      />

      <div style={{ position: "absolute", inset: 0, padding: pad, display: "flex", flexDirection: "column", zIndex: 2 }}>
        {/* Top: logo + name in white */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, color: "#FFF" }}>
          <LogoBlock logoUrl={cfg.logoUrl} businessName={cfg.businessName} color="#FFF" size={sq ? 44 : (tbl ? 48 : 52)} />
          <div style={{ fontSize: sq ? 16 : (tbl ? 18 : 20), fontWeight: 800, color: "#FFF", lineHeight: 1.1 }}>
            {cfg.businessName || "Your business"}
          </div>
        </div>

        {/* Reward callout */}
        <div style={{ marginTop: sq ? 10 : (tbl ? 14 : 18), color: "#FFF" }}>
          <div style={{ fontSize: 11, letterSpacing: 3, textTransform: "uppercase", fontWeight: 800, opacity: 0.9 }}>
            Exclusive offer
          </div>
          <div
            style={{
              fontSize: sq ? 32 : (tbl ? 38 : 44),
              fontWeight: 900,
              lineHeight: 0.98,
              marginTop: 4,
              color: "#FFF",
            }}
          >
            {cfg.rewardHighlight || "WIN A REWARD"}
          </div>
        </div>

        {/* QR card */}
        <div
          style={{
            marginTop: sq ? 12 : (tbl ? 20 : 28),
            background: "#fff",
            borderRadius: 20,
            border: "3px solid #1A1A1A",
            boxShadow: "8px 8px 0 0 #1A1A1A",
            padding: sq ? 16 : (tbl ? 20 : 26),
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <div style={{ padding: 12, background: "#FFF", borderRadius: 12, border: "2px solid #1A1A1A" }}>
            <QRCodeCanvas value={cfg.publicUrl} size={qrSize} bgColor="#FFFFFF" fgColor="#1A1A1A" level="M" includeMargin={false} />
          </div>
          <div style={{ marginTop: 12, fontSize: sq ? 14 : (tbl ? 15 : 17), fontWeight: 800, textAlign: "center", lineHeight: 1.2 }}>
            {cfg.headline}
          </div>
          {cfg.subtext && a4 && (
            <div style={{ marginTop: 6, fontSize: 13, color: "#4A4A4A", textAlign: "center", lineHeight: 1.35 }}>
              {cfg.subtext}
            </div>
          )}
        </div>

        {/* Steps */}
        <div
          style={{
            marginTop: "auto",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: 8,
            paddingTop: 12,
          }}
        >
          {[
            { n: "1", t: "Scan" },
            { n: "2", t: "Share" },
            { n: "3", t: "Win" },
          ].map((s, i) => (
            <React.Fragment key={s.n}>
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 5,
                  padding: "5px 10px",
                  borderRadius: 999,
                  background: "#FFE9A8",
                  border: "2px solid #1A1A1A",
                  fontSize: 11,
                  fontWeight: 800,
                }}
              >
                <span
                  style={{
                    background: "#1A1A1A",
                    color: "#FFE9A8",
                    height: 16,
                    width: 16,
                    borderRadius: "50%",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 10,
                  }}
                >
                  {s.n}
                </span>
                {s.t}
              </div>
              {i < 2 && <span style={{ color: "#1A1A1A", fontWeight: 800 }}>→</span>}
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
});

/* ---------- 3. Premium (dark) ---------- */
const PremiumPoster = forwardRef(({ cfg, format = "a4" }, ref) => {
  const sq = format === "square";
  const tbl = format === "poster";
  const a4 = format === "a4";
  
  const size = sq ? SQUARE : (tbl ? POSTER : A4);
  const pad = sq ? 32 : (tbl ? 36 : 42);
  const qrSize = sq ? 340 : (tbl ? 300 : 340);

  return (
    <div ref={ref} style={{ ...wrapStyle(size.w, size.h, "#0F0F10"), color: "#F5F2E8" }}>
      <div
        style={{
          position: "absolute",
          top: -80,
          right: -80,
          width: 240,
          height: 240,
          borderRadius: "50%",
          background: "rgba(212,175,55,0.12)",
        }}
      />
      <div style={{ position: "absolute", inset: 0, padding: pad, display: "flex", flexDirection: "column" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <LogoBlock logoUrl={cfg.logoUrl} businessName={cfg.businessName} color="#D4AF37" size={sq ? 52 : (tbl ? 56 : 60)} />
          <div
            style={{
              fontSize: 9,
              letterSpacing: 3,
              textTransform: "uppercase",
              fontWeight: 700,
              color: "#D4AF37",
            }}
          >
            Exclusive experience
          </div>
        </div>

        <div style={{ marginTop: sq ? 18 : (tbl ? 24 : 32) }}>
          <div
            style={{
              fontSize: 12,
              letterSpacing: 3,
              textTransform: "uppercase",
              color: "#D4AF37",
              fontWeight: 700,
            }}
          >
            {cfg.businessName || "Your business"}
          </div>
          {!sq && (
            <div style={{ fontSize: sq ? 22 : (tbl ? 24 : 28), fontWeight: 800, lineHeight: 1.15, marginTop: 8, color: "#F5F2E8" }}>
              {cfg.headline}
            </div>
          )}
          {cfg.subtext && a4 && (
            <div style={{ fontSize: 14, color: "#A8A49A", marginTop: 8, lineHeight: 1.4 }}>{cfg.subtext}</div>
          )}
        </div>

        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div
            style={{
              padding: 16,
              background: "#F5F2E8",
              borderRadius: 16,
              boxShadow: "0 0 0 2px #D4AF37",
            }}
          >
            <QRCodeCanvas value={cfg.publicUrl} size={qrSize} bgColor="#F5F2E8" fgColor="#0F0F10" level="M" includeMargin={false} />
          </div>
        </div>

        {cfg.rewardHighlight && (
          <div style={{ textAlign: "center", marginBottom: 10 }}>
            <span style={{ fontSize: sq ? 14 : (tbl ? 15 : 17), fontWeight: 800, color: "#D4AF37", letterSpacing: 1 }}>{cfg.rewardHighlight}</span>
          </div>
        )}
        <div
          style={{
            textAlign: "center",
            fontSize: 10,
            color: "#A8A49A",
            letterSpacing: 2.5,
            textTransform: "uppercase",
          }}
        >
          Scan · Share · Enjoy
        </div>
      </div>
    </div>
  );
});

/* ---------- 4. Fun ---------- */
const FunPoster = forwardRef(({ cfg, format = "a4" }, ref) => {
  const sq = format === "square";
  const tbl = format === "poster";
  const a4 = format === "a4";
  
  const size = sq ? SQUARE : (tbl ? POSTER : A4);
  const pad = sq ? 24 : (tbl ? 28 : 32);
  const qrSize = sq ? 320 : (tbl ? 280 : 320);

  return (
    <div ref={ref} style={wrapStyle(size.w, size.h, "#FFE9A8")}>
      {/* decorative circles */}
      <div
        style={{
          position: "absolute",
          top: -60,
          left: -60,
          width: 180,
          height: 180,
          borderRadius: "50%",
          background: "#B5E2FA",
          border: "3px solid #1A1A1A",
        }}
      />
      <div
        style={{
          position: "absolute",
          bottom: -70,
          right: -70,
          width: 210,
          height: 210,
          borderRadius: "50%",
          background: "#E2D4F0",
          border: "3px solid #1A1A1A",
        }}
      />
      <div
        style={{
          position: "absolute",
          top: 80,
          right: 30,
          width: 70,
          height: 70,
          borderRadius: "50%",
          background: cfg.primaryColor,
          border: "3px solid #1A1A1A",
        }}
      />

      <div style={{ position: "relative", inset: 0, padding: pad, display: "flex", flexDirection: "column", height: "100%" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <LogoBlock logoUrl={cfg.logoUrl} businessName={cfg.businessName} color={cfg.primaryColor} size={sq ? 46 : (tbl ? 50 : 54)} />
          <div style={{ fontSize: sq ? 18 : (tbl ? 20 : 22), fontWeight: 800, lineHeight: 1.1 }}>{cfg.businessName || "Your business"}</div>
        </div>

        <div style={{ marginTop: sq ? 12 : (tbl ? 16 : 20) }}>
          <div
            style={{
              display: "inline-block",
              background: "#1A1A1A",
              color: "#FFE9A8",
              padding: "5px 12px",
              borderRadius: 999,
              fontSize: 11,
              letterSpacing: 2,
              textTransform: "uppercase",
              fontWeight: 800,
            }}
          >
            Spin & win
          </div>
          {!sq && (
            <div style={{ fontSize: sq ? 24 : (tbl ? 26 : 30), fontWeight: 900, lineHeight: 1.05, marginTop: 10 }}>{cfg.headline}</div>
          )}
        </div>

        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div
            style={{
              transform: "rotate(-2deg)",
              padding: 14,
              background: "#fff",
              borderRadius: 16,
              border: "3px solid #1A1A1A",
              boxShadow: "6px 6px 0 0 #1A1A1A",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <QRCodeCanvas value={cfg.publicUrl} size={qrSize} bgColor="#FFFFFF" fgColor="#1A1A1A" level="M" includeMargin={false} />
            {cfg.rewardHighlight && (
              <div
                style={{
                  marginTop: 10,
                  fontSize: 14,
                  fontWeight: 900,
                  color: cfg.primaryColor,
                  textAlign: "center",
                }}
              >
                {cfg.rewardHighlight}
              </div>
            )}
          </div>
        </div>

        <div style={{ textAlign: "center", fontSize: 13, fontWeight: 800 }}>
          Scan · Share your feedback · Spin to win
        </div>
      </div>
    </div>
  );
});

MinimalPoster.displayName = "MinimalPoster";
BoldPoster.displayName = "BoldPoster";
PremiumPoster.displayName = "PremiumPoster";
FunPoster.displayName = "FunPoster";

export const TEMPLATES = [
  { id: "minimal", name: "Minimal", desc: "Clean white", Component: MinimalPoster },
  { id: "bold", name: "Bold Offer", desc: "Big reward callout", Component: BoldPoster },
  { id: "premium", name: "Premium", desc: "Dark & elegant", Component: PremiumPoster },
  { id: "fun", name: "Fun", desc: "Playful pastels", Component: FunPoster },
];

// A4, POSTER, SQUARE already exported above
